<template>
  <div v-if="visible" class="camera-modal-backdrop">
    <div :class="['camera-modal-card', { 'camera-modal-card--recording': recordingActive }]">
      <header class="camera-modal-header">
        <h5 class="camera-modal-title">{{ title }}</h5>
        <div class="header-actions">
          <button
            class="style-toggle"
            :class="{ 'style-toggle--disabled': recordingActive || finalizingRecording }"
            :disabled="recordingActive || finalizingRecording"
            @click="toggleStyle"
            title="Tema claro/oscuro"
          >
            <i :class="selectedStyle === 'dark' ? 'fa-regular fa-sun' : 'fa-solid fa-moon'" aria-hidden="true"></i>
          </button>
          <button
            class="style-toggle test-toggle"
            :class="{ 'style-toggle--disabled': recordingActive || finalizingRecording }"
            :disabled="recordingActive || finalizingRecording || testingBandwidth"
            @click="openBandwidthModal"
            :title="bandwidthTooltipLabel"
          >
            <i :class="testingBandwidth ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-gauge-high'" aria-hidden="true"></i>
          </button>
          <button
            class="style-toggle live-toggle"
            :class="{
              'style-toggle--disabled': liveBusy || recordingActive || finalizingRecording,
              'live-toggle--active': liveActive,
            }"
            :disabled="liveBusy || recordingActive || finalizingRecording"
            @click="handleLiveToggle"
            :title="liveTooltipLabel"
          >
            <i :class="liveBusy ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-tower-broadcast'" aria-hidden="true"></i>
          </button>
          <button class="camera-modal-close" @click="handleClose">×</button>
        </div>
      </header>

      <div class="camera-modal-body">
        <p class="camera-modal-help">{{ helper }}</p>

        <div v-if="error" class="camera-modal-error">{{ error }}</div>

        <div v-if="!hasActiveStream" class="camera-selectors" style="display:none;"></div>

        <div
          class="camera-preview"
          :class="[activePreviewClass, { 'camera-preview--empty': !stream, 'camera-preview--fullscreen': fullscreenPreview }]"
        >
          <canvas
            ref="canvasEl"
            class="camera-preview-canvas"
            v-show="Boolean(stream)"
          ></canvas>
          <video
            ref="videoEl"
            autoplay
            playsinline
            muted
            class="camera-preview-video"
            aria-hidden="true"
          ></video>
          <div v-if="!stream" class="camera-preview-placeholder">{{ previewPlaceholder }}</div>
          <div v-if="!stream && hasAnyOverlay" class="camera-overlay" :style="overlayStyleVars">
            <div v-if="hasBrandingOverlay || hasTimerOverlay" class="camera-overlay-row camera-overlay-row--top">
              <div
                v-if="activeBrandLogo"
                class="camera-overlay-badge camera-overlay-badge--left"
                :class="{ 'camera-overlay-badge--embedded': brandLogoHasBadge }"
              >
                <img :src="activeBrandLogo" alt="App logo" />
              </div>
              <div v-if="hasTimerOverlay" class="camera-overlay-badge camera-overlay-badge--center">
                <div v-if="isTimerVs" class="timer-pill timer-pill--vs">
                  <span class="timer-pill__side">{{ safeTimerValue(timerLeftValue || timerValue) }}</span>
                  <span class="timer-pill__vs">VS</span>
                  <span class="timer-pill__side">{{ safeTimerValue(timerRightValue || timerValue) }}</span>
                </div>
                <div v-else class="timer-pill">
                  <span class="timer-pill__value">{{ safeTimerValue(timerValue) }}</span>
                </div>
              </div>
              <div v-if="props.competitionName" class="camera-overlay-badge camera-overlay-badge--right">
                <span>{{ props.competitionName }}</span>
              </div>
            </div>
            <div v-if="hasLowerOverlay" class="camera-overlay-row camera-overlay-row--bottom">
              <template v-if="hasVsOverlay">
                <div class="camera-overlay-lower camera-overlay-lower--left">
                  <img
                    v-if="props.vsLeftFlagUrl"
                    :src="props.vsLeftFlagUrl"
                    class="camera-overlay-flag-image"
                    alt=""
                    aria-hidden="true"
                  />
                  <div class="camera-overlay-info">
                    <span class="camera-overlay-info__name">{{ props.vsLeftName }}</span>
                  </div>
                </div>
                <div v-if="props.stageSubtitle" class="camera-overlay-lower camera-overlay-lower--center">
                  <span class="camera-overlay-info__value">{{ props.stageSubtitle }}</span>
                </div>
                <div class="camera-overlay-lower camera-overlay-lower--right">
                  <div class="camera-overlay-info camera-overlay-info--right">
                    <span class="camera-overlay-info__name">{{ props.vsRightName }}</span>
                  </div>
                  <img
                    v-if="props.vsRightFlagUrl"
                    :src="props.vsRightFlagUrl"
                    class="camera-overlay-flag-image camera-overlay-flag-image--right"
                    alt=""
                    aria-hidden="true"
                  />
                </div>
              </template>
              <template v-else>
                <div
                  v-if="props.athleteName || props.athleteFlagUrl"
                  class="camera-overlay-lower camera-overlay-lower--left"
                >
                  <img
                    v-if="props.athleteFlagUrl"
                    :src="props.athleteFlagUrl"
                    class="camera-overlay-flag-image"
                    alt=""
                    aria-hidden="true"
                  />
                  <div class="camera-overlay-info">
                    <span class="camera-overlay-info__name">{{ props.athleteName }}</span>
                  </div>
                </div>
                <div
                  v-if="props.stageSubtitle"
                  class="camera-overlay-lower camera-overlay-lower--right"
                >
                  <span class="camera-overlay-info__value">{{ props.stageSubtitle }}</span>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>

      <div v-if="showBandwidthModal" class="bandwidth-modal-backdrop">
        <div class="bandwidth-modal-card">
          <div class="bandwidth-modal-header">
            <h6>{{ bandwidthTitle }}</h6>
            <button class="bandwidth-close" @click="closeBandwidthModal">×</button>
          </div>
          <div class="bandwidth-modal-body">
            <p class="bandwidth-hint">{{ bandwidthHint }}</p>
            <div v-if="bandwidthError" class="bandwidth-error">{{ bandwidthError }}</div>
            <div v-else-if="testingBandwidth" class="bandwidth-loading">
              <i class="fa-solid fa-spinner fa-spin" aria-hidden="true"></i>
              <span>{{ bandwidthMeasuringLabel }}</span>
            </div>
            <div v-else-if="bandwidthResult" class="bandwidth-result">
              <div class="result-row"><span>{{ bandwidthSizeLabel }}</span><strong>{{ formatBytes(bandwidthBytes) }}</strong></div>
              <div class="result-row"><span>{{ bandwidthDurationLabel }}</span><strong>{{ formatMs(bandwidthResult.duration_ms) }}</strong></div>
              <div class="result-row"><span>{{ bandwidthUploadLabel }}</span><strong>{{ formatUploadMbps(computedUploadMbps) }}</strong></div>
              <div
                class="result-row"
                v-for="est in bandwidthEstimateRows"
                :key="est.key"
              >
                <span>{{ est.label }}</span><strong>{{ formatSeconds(est.seconds) }}</strong>
              </div>
              <div
                class="result-row"
                v-for="est in bandwidthVideoEstimateRows"
                :key="est.key"
              >
                <span>{{ est.label }}</span><strong>{{ formatSeconds(est.seconds) }}</strong>
              </div>
            </div>
            <div class="bandwidth-actions">
              <button class="btn btn-sm btn-primary" :disabled="testingBandwidth" @click="runBandwidthTest">{{ bandwidthRepeatLabel }}</button>
            </div>
          </div>
        </div>
      </div>

      <footer class="camera-modal-actions">
        <div class="camera-info-bar camera-info-bar--inline">
          <template v-if="stream">
            <div class="info-chip info-chip--compact">
              <span class="info-chip__title">{{ actualLabel }}</span>
              <span class="info-chip__value">{{ actualResolution }}{{ actualFrameRate ? ` @ ${actualFrameRate} fps` : '' }}</span>
            </div>
            <div class="info-chip info-chip--compact">
              <span class="info-chip__title">Bitrate</span>
              <span class="info-chip__value">{{ recorderBitrateLabel }}</span>
            </div>
            <div v-if="recordingActive" class="info-chip info-chip--live">
              <span class="info-chip__title">Tiempo</span>
              <span class="info-chip__value">{{ recordingElapsedText }}</span>
            </div>
            <div v-if="recordingActive" class="info-chip info-chip--live">
              <span class="info-chip__title">Tamano</span>
              <span class="info-chip__value">{{ recordedSizeLabel }}</span>
            </div>
            <div v-if="recordingActive" class="info-chip info-chip--muted">
              <span class="info-chip__title">Bitrate real</span>
              <span class="info-chip__value">{{ recordingBitrateLabel }}</span>
            </div>
          </template>

          <div class="camera-action-row">
            <div class="action-buttons">
              <button
                v-if="!stream"
                class="btn btn-sm btn-outline-primary"
                :disabled="loading || recordingActive || finalizingRecording"
                @click="handleStart"
              >
                <i :class="loading ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-video'" aria-hidden="true"></i>
                <span>{{ loading ? loadingLabel : startCameraLabel }}</span>
              </button>
              <template v-else>
                <template v-if="showRecordControls">
                  <button
                    v-if="!recordingActive && !finalizingRecording"
                    class="btn btn-sm btn-danger"
                    :disabled="loading || !canRecord || hasRunningTimer"
                    @click="handleBeginRecording"
                  >
                    <i class="fa-solid fa-circle-dot" aria-hidden="true"></i>
                    <span>{{ recordLabel }}</span>
                  </button>
                  <button
                    v-else-if="recordingActive"
                    class="btn btn-sm btn-danger"
                    :disabled="loading || hasRunningTimer"
                    @click="handleFinishRecording"
                  >
                    <i class="fa-solid fa-stop" aria-hidden="true"></i>
                    <span>{{ stopLabel }}</span>
                  </button>
                  <button
                    v-else
                    class="btn btn-sm btn-outline-secondary"
                    disabled
                  >
                    <i class="fa-solid fa-spinner fa-spin" aria-hidden="true"></i>
                    <span>{{ savingLabel }}</span>
                  </button>
                </template>

                <slot
                  name="actions-extra"
                  :recording-active="recordingActive"
                  :finalizing-recording="finalizingRecording"
                  :loading="loading"
                />

                <button
                  class="btn btn-sm btn-outline-primary btn-stop-camera"
                  :disabled="loading || recordingActive || finalizingRecording"
                  @click="handleStopCamera"
                >
                  <i class="fa-solid fa-video-slash" aria-hidden="true"></i>
                  <span>{{ stopCameraLabel }}</span>
                </button>
              </template>
            </div>
          </div>
        </div>
      </footer>
    </div>
  </div>
</template>

<script setup>
import axios from 'axios';
import { ref, watch, onBeforeUnmount, onMounted, nextTick, defineProps, defineEmits, computed } from 'vue';
import { resolveApiBaseUrl } from '@/services/apiConfig';

const MIME_PRIORITY = [
  'video/webm;codecs=vp9',
  'video/webm;codecs=vp8',
  'video/webm',
  'video/mp4;codecs=h264',
  'video/mp4',
];
const QUALITY_PRESETS = {
  low: {
    constraints: {
      width: { ideal: 640 },
      height: { ideal: 360 },
      frameRate: { ideal: 24 },
    },
    recorder: {
      videoBitsPerSecond: 800000,
    },
  },
  medium: {
    constraints: {
      width: { ideal: 1280 },
      height: { ideal: 720 },
      frameRate: { ideal: 30 },
    },
    recorder: {
      videoBitsPerSecond: 2500000,
    },
  },
  max: {
    constraints: {
      width: { ideal: 1920, max: 1920 },
      height: { ideal: 1080, max: 1080 },
      frameRate: { ideal: 30, max: 30 },
    },
    recorder: {
      videoBitsPerSecond: 5500000,
    },
  },
  pro: {
    constraints: {
      width: { ideal: 1920, max: 2560 },
      height: { ideal: 1080, max: 1440 },
      frameRate: { ideal: 30, max: 60 },
    },
    recorder: {
      videoBitsPerSecond: 9000000,
    },
  },
};
const STYLE_PRESETS = {
  light: {
    previewClass: 'camera-preview--light',
    brandLogoVariant: 'primary',
    vars: {
      '--camera-overlay-top-bg': 'rgba(255, 255, 255, 0.92)',
      '--camera-overlay-top-text': '#0f172a',
      '--camera-overlay-lower-bg': 'rgba(241, 245, 249, 0.95)',
      '--camera-overlay-lower-text': '#0b132b',
      '--camera-overlay-country-color': '#475569',
      '--camera-overlay-accent': '#0f172a',
    },
  },
  dark: {
    previewClass: 'camera-preview--dark',
    brandLogoVariant: 'alternate',
    vars: {
      '--camera-overlay-top-bg': 'rgba(0, 0, 0, 0.55)',
      '--camera-overlay-top-text': '#f8fafc',
      '--camera-overlay-lower-bg': 'rgba(0, 0, 0, 0.82)',
      '--camera-overlay-lower-text': '#f8fafc',
      '--camera-overlay-country-color': '#e2e8f0',
      '--camera-overlay-accent': '#e2e8f0',
    },
  },
  custom: {
    previewClass: 'camera-preview--custom',
    brandLogoVariant: 'alternate',
    vars: {
      '--camera-overlay-top-bg': 'linear-gradient(135deg, rgba(7, 11, 23, 0.92), rgba(20, 24, 52, 0.92))',
      '--camera-overlay-top-text': '#f8fafc',
      '--camera-overlay-lower-bg': 'linear-gradient(135deg, rgba(7, 11, 23, 0.92), rgba(21, 94, 117, 0.92))',
      '--camera-overlay-lower-text': '#f8fafc',
      '--camera-overlay-country-color': '#e0f2fe',
      '--camera-overlay-accent': '#f97316',
    },
    accentBars: {
      primary: '#f97316',
      secondary: '#0ea5e9',
      alpha: 0.22,
      angle: -12,
    },
    disabled: false,
  },
};
const DEFAULT_NO_DATA_ERROR = 'No video was captured. Please try recording again.';
const hasMediaRecorder = typeof window !== 'undefined' && typeof window.MediaRecorder !== 'undefined';
const API_BASE_URL = resolveApiBaseUrl().replace(/\/$/, '');

function parseColorToRgb(color = '') {
  const val = String(color || '').trim();
  if (!val) return null;
  if (val.startsWith('#')) {
    const hex = val.replace('#', '');
    if (hex.length === 6) {
      const intVal = parseInt(hex, 16);
      return {
        r: (intVal >> 16) & 0xff,
        g: (intVal >> 8) & 0xff,
        b: intVal & 0xff,
      };
    }
  } else if (/^rgba?\(/i.test(val)) {
    const parts = val
      .replace(/^rgba?\(/i, '')
      .replace(/\)$/, '')
      .split(',')
      .map((p) => Number(p.trim()))
      .filter((n) => Number.isFinite(n));
    if (parts.length >= 3) {
      return { r: parts[0], g: parts[1], b: parts[2] };
    }
  }
  return null;
}

function luminanceFromRgb(rgb) {
  if (!rgb) return null;
  const { r, g, b } = rgb;
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
}

function computeVsColors({ accent = '', topBg = '', topText = '' } = {}) {
  const accentRgb = parseColorToRgb(accent);
  const topBgRgb = parseColorToRgb(topBg);
  const accentLum = luminanceFromRgb(accentRgb);
  const topBgLum = luminanceFromRgb(topBgRgb);
  const useTopBg = topBgLum !== null && topBgLum < 0.65;
  const bg = useTopBg
    ? topBg
    : accentLum !== null
      ? (accentLum >= 0.55 ? accent : topBg || accent || '#111827')
      : (topBg || accent || '#111827');
  const bgLum = luminanceFromRgb(parseColorToRgb(bg));
  const text = bgLum !== null && bgLum < 0.6 ? (topText || '#f8fafc') : '#0b132b';
  return { bg, text };
}

const props = defineProps({
  visible: { type: Boolean, default: false },
  title: { type: String, default: '' },
  helper: { type: String, default: '' },
  selectLabel: { type: String, default: '' },
  noDevicesLabel: { type: String, default: '' },
  previewPlaceholder: { type: String, default: '' },
  startCameraLabel: { type: String, default: '' },
  recordLabel: { type: String, default: '' },
  stopLabel: { type: String, default: '' },
  cancelLabel: { type: String, default: '' },
  loadingLabel: { type: String, default: '' },
  savingLabel: { type: String, default: '' },
  noDataLabel: { type: String, default: '' },
  stopCameraLabel: { type: String, default: '' },
  qualityLabel: { type: String, default: '' },
  qualityLowLabel: { type: String, default: '' },
  qualityMediumLabel: { type: String, default: '' },
  qualityMaxLabel: { type: String, default: '' },
  qualityProLabel: { type: String, default: 'Full HD (mejor esfuerzo)' },
  statusRecordingLabel: { type: String, default: 'Grabando' },
  statusReadyLabel: { type: String, default: 'Listo' },
  statusOffLabel: { type: String, default: 'Cámara apagada' },
  actualLabel: { type: String, default: 'Actual' },
  targetLabel: { type: String, default: 'Objetivo' },
  supportsLabel: { type: String, default: 'Soporta' },
  autoLabel: { type: String, default: 'Auto' },
  styleLabel: { type: String, default: 'Estilo' },
  styleLightLabel: { type: String, default: 'Claro' },
  styleDarkLabel: { type: String, default: 'Oscuro' },
  styleCustomLabel: { type: String, default: 'Editable (pronto)' },
  bandwidthTitle: { type: String, default: 'Test de ancho de banda' },
  bandwidthHint: { type: String, default: 'Se envía un archivo de 50MB al endpoint seguro para estimar tu subida actual.' },
  bandwidthTooltipLabel: { type: String, default: 'Test de ancho de banda' },
  bandwidthMeasuringLabel: { type: String, default: 'Midiendo...' },
  bandwidthSizeLabel: { type: String, default: 'Tamaño' },
  bandwidthDurationLabel: { type: String, default: 'Duración' },
  bandwidthUploadLabel: { type: String, default: 'Subida' },
  bandwidthEstimatePrefixLabel: { type: String, default: 'Est.' },
  bandwidthEstimate500Label: { type: String, default: 'Est. 500MB' },
  bandwidthEstimate1000Label: { type: String, default: 'Est. 1000MB' },
  bandwidthVideoEstimatePrefixLabel: { type: String, default: 'Video' },
  bandwidthVideoBitrateMbps: { type: Number, default: 9 },
  bandwidthRepeatLabel: { type: String, default: 'Repetir prueba' },
  bandwidthMissingTokenLabel: { type: String, default: 'Falta token de autenticación' },
  liveActive: { type: Boolean, default: false },
  liveBusy: { type: Boolean, default: false },
  liveTooltipLabel: { type: String, default: 'Live' },
  recordingFileName: { type: String, default: '' },
  brandLogo: { type: String, default: '' },
  brandLogoAlt: { type: String, default: '' },
  brandLogoHasBadge: { type: Boolean, default: false },
  competitionName: { type: String, default: '' },
  athleteName: { type: String, default: '' },
  athleteCountryName: { type: String, default: '' },
  athleteCountryCode: { type: String, default: '' },
  athleteFlagUrl: { type: String, default: '' },
  stageTitle: { type: String, default: '' },
  stageSubtitle: { type: String, default: '' },
  timerMode: { type: String, default: 'single' },
  timerValue: { type: String, default: '' },
  timerLeftValue: { type: String, default: '' },
  timerRightValue: { type: String, default: '' },
  showTimer: { type: Boolean, default: false },
  vsLeftName: { type: String, default: '' },
  vsLeftFlagUrl: { type: String, default: '' },
  vsRightName: { type: String, default: '' },
  vsRightFlagUrl: { type: String, default: '' },
  startTrigger: { type: Number, default: 0 },
  showRecordControls: { type: Boolean, default: true },
  canRecord: { type: Boolean, default: true },
  hasRunningTimer: { type: Boolean, default: false },
  authToken: { type: String, default: '' },
  keepStreamOnHide: { type: Boolean, default: true },
});

const emit = defineEmits(['begin-recording', 'finish-recording', 'cancel', 'camera-ready', 'camera-stopped', 'request-live-toggle']);

const overlayFlagImageCache = new Map();

const devices = ref([]);
const devicesLoaded = ref(false);
const selectedDeviceId = ref('');
const selectedQuality = ref('pro');
const selectedStyle = ref('light');
const stream = ref(null);
const composedStream = ref(null);
const canvasEl = ref(null);
const brandLogoImage = ref(null);
const error = ref('');
const loading = ref(false);
const videoEl = ref(null);
const recordingActive = ref(false);
const finalizingRecording = ref(false);
const mediaRecorder = ref(null);
const recordedChunks = ref([]);
const recordedSizeBytes = ref(0);
const recordingMime = ref('');
const stopReason = ref('user');
const trackCapabilities = ref(null);
const trackSettings = ref(null);
let trackMonitorHandle = null;
const recordingStartedAt = ref(0);
const recordingElapsedText = ref('00:00');
let recordingTimerHandle = null;
let canvasCtx = null;
let compositionFrameHandle = null;
let lastUsedRecorderStream = null;
const isLandscape = ref(false);
const isMobileViewport = ref(false);
const fullscreenPreview = computed(() => isMobileViewport.value && isLandscape.value);
let orientationMediaQuery = null;
let screenOrientationHandle = null;

function computeIsLandscape() {
  const vv = typeof visualViewport !== 'undefined' ? visualViewport : null;
  const vw = vv?.width || (typeof window !== 'undefined' ? window.innerWidth : 0);
  const vh = vv?.height || (typeof window !== 'undefined' ? window.innerHeight : 0);
  const mediaLandscape = orientationMediaQuery ? orientationMediaQuery.matches : null;
  if (mediaLandscape !== null) {
    isLandscape.value = mediaLandscape || vw > vh;
  } else {
    isLandscape.value = vw > vh;
  }
}

function updateOrientation() {
  computeIsLandscape();
}

function updateViewportFlags() {
  const vv = typeof visualViewport !== 'undefined' ? visualViewport : null;
  const width = vv?.width || (typeof window !== 'undefined' ? window.innerWidth : 0);
  isMobileViewport.value = width <= 900;
}

function handleLiveToggle() {
  if (props.liveBusy || recordingActive.value || finalizingRecording.value) return;
  emit('request-live-toggle');
}

onMounted(() => {
  if (typeof window === 'undefined') return;
  orientationMediaQuery = window.matchMedia('(orientation: landscape)');
  updateOrientation();
  updateViewportFlags();
  orientationMediaQuery.addEventListener('change', updateOrientation);
  window.addEventListener('orientationchange', computeIsLandscape);
  if (window.screen?.orientation?.addEventListener) {
    screenOrientationHandle = window.screen.orientation;
    screenOrientationHandle.addEventListener('change', computeIsLandscape);
  }
  window.addEventListener('resize', updateViewportFlags, { passive: true });
  if (typeof visualViewport !== 'undefined') {
    visualViewport.addEventListener('resize', computeIsLandscape, { passive: true });
  }
});
const showBandwidthModal = ref(false);
const testingBandwidth = ref(false);
const bandwidthResult = ref(null);
const bandwidthError = ref('');
const bandwidthBytes = computed(() => {
  const res = bandwidthResult.value || {};
  const val = Number(res.server_bytes ?? res.size_bytes ?? 0);
  return Number.isFinite(val) ? val : 0;
});
const computedUploadMbps = computed(() => {
  const res = bandwidthResult.value || {};
  const fromServer = Number(res.upload_mbps);
  if (Number.isFinite(fromServer) && fromServer > 0) return fromServer;
  const size = Number(res.server_bytes ?? res.size_bytes);
  const durationMs = Number(res.duration_ms);
  if (size > 0 && durationMs > 0) {
    const mbps = (size * 8) / (durationMs * 1000);
    return mbps > 0 ? mbps : null;
  }
  return null;
});
const bandwidthEstimates = computed(() => {
  const res = bandwidthResult.value || {};
  const serverEst = res.estimated_seconds;
  if (serverEst && typeof serverEst === 'object') {
    const normalized = {};
    Object.entries(serverEst).forEach(([k, v]) => {
      const num = Number(v);
      if (Number.isFinite(num)) normalized[k] = num;
    });
    if (Object.keys(normalized).length) return normalized;
  }

  const mbps = Number(computedUploadMbps.value);
  if (!mbps || Number.isNaN(mbps) || mbps <= 0) return null;
  const calc = (mb) => {
    const bytes = mb * 1024 * 1024;
    const bits = bytes * 8;
    return bits / (mbps * 1_000_000);
  };
  return {
    100: calc(100),
    200: calc(200),
    500: calc(500),
    1000: calc(1000),
  };
});
const bandwidthEstimateRows = computed(() => {
  const est = bandwidthEstimates.value;
  if (!est) return [];
  return Object.entries(est)
    .filter(([, v]) => Number.isFinite(Number(v)))
    .map(([mb, seconds]) => ({
      key: `mb-${mb}`,
      label: `${props.bandwidthEstimatePrefixLabel} ${mb}MB`,
      seconds: Number(seconds),
    }));
});
const bandwidthVideoEstimateRows = computed(() => {
  const uploadMbps = Number(computedUploadMbps.value);
  const bitrate = Number(props.bandwidthVideoBitrateMbps);
  if (!uploadMbps || uploadMbps <= 0 || !bitrate || bitrate <= 0) return [];

  const durations = [90, 120, 150];
  return durations.map((dur) => {
    const seconds = (bitrate * dur) / uploadMbps;
    return {
      key: `video-${dur}s-${bitrate}`,
      label: `${props.bandwidthVideoEstimatePrefixLabel} ${dur}s @ ${bitrate} Mbps`,
      seconds,
    };
  });
});
const activeStylePreset = computed(() => STYLE_PRESETS[selectedStyle.value] || STYLE_PRESETS.light);
const activePreviewClass = computed(() => activeStylePreset.value?.previewClass || '');
const overlayStyleVars = computed(() => activeStylePreset.value?.vars || {});
const isTimerVs = computed(() => (props.timerMode || '').toLowerCase() === 'vs');
const hasTimerOverlay = computed(() => props.showTimer && Boolean(props.timerValue || props.timerLeftValue || props.timerRightValue));
const activeBrandLogo = computed(() => {
  const variant = activeStylePreset.value?.brandLogoVariant;
  if (variant === 'alternate' && props.brandLogoAlt) {
    return props.brandLogoAlt;
  }
  return props.brandLogo || props.brandLogoAlt || '';
});
const hasBrandingOverlay = computed(
  () => Boolean(activeBrandLogo.value || props.competitionName)
);
const brandLogoHasBadge = computed(() => Boolean(props.brandLogoHasBadge && activeBrandLogo.value));
const athleteCountryLine = computed(() => '');
const hasVsOverlay = computed(() => Boolean(props.vsLeftName || props.vsRightName));
const hasLowerOverlay = computed(
  () => Boolean(
    props.athleteName ||
      athleteCountryLine.value ||
      props.athleteFlagUrl ||
      props.stageSubtitle ||
      hasVsOverlay.value
  )
);
const hasAnyOverlay = computed(() => hasBrandingOverlay.value || hasLowerOverlay.value || hasTimerOverlay.value);
const safeTimerValue = (value) => {
  const str = (value || '').toString().trim();
  return str || '00:00';
};

async function runBandwidthTest() {
  bandwidthError.value = '';
  bandwidthResult.value = null;
  if (!props.authToken) {
    bandwidthError.value = props.bandwidthMissingTokenLabel || 'Falta token de autenticación';
    return;
  }
  testingBandwidth.value = true;
  try {
    const size = 50 * 1024 * 1024; // 50MB para respetar límite del backend
    const file = new File([new Uint8Array(size)], 'bandwidth-test.bin', { type: 'application/octet-stream' });
    const form = new FormData();
    form.append('payload', file);

    const endpoint = `${API_BASE_URL}/bandwidth/test`;
    const { data } = await axios.post(endpoint, form, {
      headers: {
        Authorization: `Bearer ${props.authToken}`,
        'Content-Type': 'multipart/form-data',
      },
    });

    bandwidthResult.value = data || null;
  } catch (err) {
    bandwidthError.value = err?.response?.data?.message || err?.message || 'No se pudo completar la prueba.';
  } finally {
    testingBandwidth.value = false;
  }
}

function openBandwidthModal() {
  showBandwidthModal.value = true;
  runBandwidthTest();
}

function closeBandwidthModal() {
  showBandwidthModal.value = false;
}

watch(
  () => props.startTrigger,
  (val, oldVal) => {
    if (val > oldVal) {
      attemptAutoRecord();
    }
  }
);

function toggleStyle() {
  selectedStyle.value = selectedStyle.value === 'dark' ? 'light' : 'dark';
}

function formatMs(ms) {
  if (ms === undefined || ms === null) return '-';
  const seconds = ms / 1000;
  return `${seconds.toFixed(2)} s`;
}

function formatSeconds(sec) {
  if (sec === undefined || sec === null) return '-';
  const n = Number(sec);
  if (Number.isNaN(n)) return '-';
  if (n < 60) return `${n.toFixed(1)} s`;
  const minutes = Math.floor(n / 60);
  const remain = n % 60;
  return `${minutes}m ${remain.toFixed(0)}s`;
}
function formatUploadMbps(val) {
  if (!val || Number.isNaN(val)) return '-';
  return Number(val).toFixed(2);
}
const actualResolution = computed(() => {
  const width = trackSettings.value?.width;
  const height = trackSettings.value?.height;
  return width && height ? `${width}x${height}` : '-';
});
const actualFrameRate = computed(() => {
  const fps = trackSettings.value?.frameRate;
  return fps ? Math.round(fps) : null;
});
const hasActiveStream = computed(() => Boolean(stream.value));
const recorderBitrate = computed(() => getSelectedQualityPreset()?.recorder?.videoBitsPerSecond || null);
const recorderBitrateLabel = computed(() => formatBitrate(recorderBitrate.value) || props.autoLabel);
const recordedSizeLabel = computed(() => formatBytes(recordedSizeBytes.value) || props.autoLabel);
const recordingBitrateLabel = computed(() => formatBitrate(estimateRecordingBitrate()) || props.autoLabel);

watch(
  activeBrandLogo,
  (src) => {
    loadBrandLogoImage(src);
  },
  { immediate: true }
);

watch(
  () => [props.athleteFlagUrl, props.vsLeftFlagUrl, props.vsRightFlagUrl],
  (urls) => {
    preloadOverlayFlagImages(urls);
  },
  { immediate: true }
);

watch(
  () => props.visible,
  (val) => {
    if (val) {
      resetState();
      initDevices();
    } else if (!props.keepStreamOnHide) {
      stopRecorder('cleanup');
      stopStream('visible-false');
    }
  }
);

function resetState() {
  error.value = '';
  loading.value = false;
  devices.value = [];
  devicesLoaded.value = false;
  selectedDeviceId.value = '';
  selectedQuality.value = 'pro';
  selectedStyle.value = 'light';
  recordingActive.value = false;
  finalizingRecording.value = false;
  recordedChunks.value = [];
  recordedSizeBytes.value = 0;
  recordingMime.value = '';
  stopReason.value = 'user';
  lastUsedRecorderStream = null;
  // stream stopped in watcher if closing; keep current stream if reopening while visible
}


function loadBrandLogoImage(src) {
  if (typeof window === 'undefined' || typeof Image === 'undefined') {
    brandLogoImage.value = null;
    return;
  }
  if (!src) {
    brandLogoImage.value = null;
    return;
  }
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    brandLogoImage.value = img;
  };
  img.onerror = () => {
    brandLogoImage.value = null;
  };
  img.src = src;
}

async function initDevices() {
  if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
    error.value = window.isSecureContext
      ? (props.noDevicesLabel || 'No cameras found')
      : 'Camera access requires HTTPS or running on localhost.';
    devicesLoaded.value = true;
    return;
  }
  try {
    // Try to prompt for permission up front so enumerateDevices returns results.
    if (!devicesLoaded.value) {
      try {
        const tempStream = await navigator.mediaDevices.getUserMedia({ video: true });
        tempStream.getTracks().forEach((t) => t.stop());
      } catch (permErr) {
        devicesLoaded.value = true;
        error.value = props.noDevicesLabel || 'No cameras found';
        return;
      }
    }

    const list = await navigator.mediaDevices.enumerateDevices();
    const videoInputs = list.filter((d) => d.kind === 'videoinput');
    devices.value = videoInputs;
    devicesLoaded.value = true;
    if (!videoInputs.length) {
      error.value = props.noDevicesLabel || 'No cameras found';
      return;
    }
    if (!selectedDeviceId.value) {
      selectedDeviceId.value = videoInputs[0].deviceId;
    }
  } catch (err) {
    devicesLoaded.value = true;
    error.value = props.noDevicesLabel || 'No cameras found';
  }
}

function ensureOverlayFlagImage(url) {
  const normalizedUrl = String(url || '').trim();
  if (!normalizedUrl || typeof window === 'undefined' || typeof Image === 'undefined') {
    return null;
  }
  const cached = overlayFlagImageCache.get(normalizedUrl);
  if (cached) {
    return cached;
  }
  const img = new Image();
  img.crossOrigin = 'anonymous';
  const entry = { img, status: 'loading' };
  img.onload = () => {
    entry.status = 'loaded';
  };
  img.onerror = () => {
    entry.status = 'error';
  };
  img.src = normalizedUrl;
  overlayFlagImageCache.set(normalizedUrl, entry);
  return entry;
}

function preloadOverlayFlagImages(urls = []) {
  urls
    .map((url) => String(url || '').trim())
    .filter(Boolean)
    .forEach((url) => ensureOverlayFlagImage(url));
}

function getOverlayFlagImage(url) {
  const entry = ensureOverlayFlagImage(url);
  return entry?.status === 'loaded' ? entry.img : null;
}

async function handleStart() {
  if (!navigator.mediaDevices || typeof navigator.mediaDevices.getUserMedia !== 'function') {
    error.value = window.isSecureContext
      ? (props.noDevicesLabel || 'Camera not available.')
      : 'Camera access requires HTTPS or running on localhost.';
    return;
  }
  if (stream.value && stream.value.getTracks().some((t) => t.readyState === 'live')) {
    // Stream ya activo; solo aseguramos monitoreo y anexamos si falta
    startTrackMonitor(stream.value);
    await attachStream(stream.value);
    return;
  }
  error.value = '';
  loading.value = true;
  try {
    stopRecorder('cleanup');
    const mediaStream = await getPreferredStream();
    stream.value = mediaStream;
    await attachStream(mediaStream);
    startTrackMonitor(mediaStream);
    await loadDevices(mediaStream);
    emit('camera-ready', { deviceId: selectedDeviceId.value, stream: mediaStream });
  } catch (err) {
    error.value = props.noDevicesLabel || 'Camera not available.';
  } finally {
    loading.value = false;
  }
}

async function attachStream(mediaStream) {
  await nextTick();
  const video = videoEl.value;
  if (!video) return;
  video.srcObject = mediaStream;
  const handleLoaded = () => {
    video.removeEventListener('loadedmetadata', handleLoaded);
    syncCanvasWithVideo();
  };
  video.addEventListener('loadedmetadata', handleLoaded);
  if (video.readyState >= 2 && video.videoWidth && video.videoHeight) {
    handleLoaded();
  }
  try {
    await video.play?.();
  } catch (err) {
    /* ignored */
  }
}

async function loadDevices(activeStream) {
  try {
    const list = await navigator.mediaDevices.enumerateDevices();
    const videoInputs = list.filter((d) => d.kind === 'videoinput');
    devices.value = videoInputs;
    devicesLoaded.value = true;
    if (!videoInputs.length) {
      error.value = props.noDevicesLabel || 'No cameras found';
      return;
    }
    const activeDeviceId = activeStream?.getVideoTracks?.()[0]?.getSettings?.().deviceId;
    if (activeDeviceId) {
      selectedDeviceId.value = activeDeviceId;
    } else if (!selectedDeviceId.value) {
      selectedDeviceId.value = videoInputs[0].deviceId;
    }
  } catch (err) {
    error.value = props.noDevicesLabel || 'No cameras found';
  }
}

function getRecordingStream() {
  return composedStream.value || stream.value;
}

function syncCanvasWithVideo() {
  if (!canvasEl.value || !videoEl.value) return;
  const video = videoEl.value;
  const width = video.videoWidth || 1280;
  const height = video.videoHeight || 720;
  if (!width || !height) return;
  const canvas = canvasEl.value;
  if (canvas.width !== width || canvas.height !== height) {
    canvas.width = width;
    canvas.height = height;
  }
  startCompositionLoop();
}

function startCompositionLoop() {
  if (!canvasEl.value) return;
  const ctx = canvasEl.value.getContext('2d');
  if (!ctx) return;
  canvasCtx = ctx;
  if (compositionFrameHandle) {
    cancelAnimationFrame(compositionFrameHandle);
  }
  compositionFrameHandle = requestAnimationFrame(drawCompositionFrame);
  refreshComposedStream();
}

function drawCompositionFrame() {
  if (!canvasCtx || !canvasEl.value) return;
  const canvas = canvasEl.value;
  const ctx = canvasCtx;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  renderBaseFrame(ctx, canvas.width, canvas.height);
  renderCanvasOverlay(ctx, canvas.width, canvas.height);
  compositionFrameHandle = requestAnimationFrame(drawCompositionFrame);
}

function renderBaseFrame(ctx, width, height) {
  const video = videoEl.value;
  if (video && video.readyState >= 2) {
    ctx.drawImage(video, 0, 0, width, height);
  } else {
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, width, height);
  }
}

function refreshComposedStream() {
  stopComposedStream();
  const canvas = canvasEl.value;
  if (!canvas || typeof canvas.captureStream !== 'function') {
    composedStream.value = null;
    return;
  }
  const frameRate = getSelectedQualityPreset()?.constraints?.frameRate?.ideal || 30;
  const canvasStream = canvas.captureStream(frameRate);
  composedStream.value = canvasStream;
}

function stopComposedStream() {
  if (!composedStream.value) return;
  composedStream.value.getTracks().forEach((track) => track.stop());
  composedStream.value = null;
}

function teardownComposition() {
  if (compositionFrameHandle) {
    cancelAnimationFrame(compositionFrameHandle);
    compositionFrameHandle = null;
  }
  canvasCtx = null;
  stopComposedStream();
}

function renderCanvasOverlay(ctx, width, height) {
  if (!hasAnyOverlay.value) return;
  const styleVars = overlayStyleVars.value || {};
  const margin = 0;
  const base = Math.min(width, height);
  const topHeight = clamp(Math.round(base * 0.08), 54, 72);
  const lowerHeight = clamp(Math.round(base * 0.11), 72, 92);
  const gap = clamp(Math.round(width * 0.012), 12, 20);
  const topBg = styleVars['--camera-overlay-top-bg'] || 'rgba(0, 0, 0, 0.55)';
  const topText = styleVars['--camera-overlay-top-text'] || '#fff';
  const lowerBg = styleVars['--camera-overlay-lower-bg'] || 'rgba(7, 16, 45, 0.78)';
  const lowerText = styleVars['--camera-overlay-lower-text'] || '#fff';
  // countryColor kept for backward compatibility; country text is disabled
  // eslint-disable-next-line no-unused-vars
  const countryColor = styleVars['--camera-overlay-country-color'] || 'rgba(255, 255, 255, 0.85)';
  const accentColor = styleVars['--camera-overlay-accent'] || '#fff';
  const { bg: vsBgColor, text: vsTextColor } = computeVsColors({ accent: accentColor, topBg, topText });
  const accentBars = activeStylePreset.value?.accentBars;
  const hasVs = hasVsOverlay.value;
  const hasLeft = hasVs
    ? Boolean(props.vsLeftName || props.vsLeftFlagUrl)
    : Boolean(props.athleteName || props.athleteCountryName || props.athleteFlagUrl);
  const hasRight = hasVs
    ? Boolean(props.vsRightName || props.vsRightFlagUrl)
    : Boolean(props.stageSubtitle);
  const hasCenter = hasVs ? Boolean(props.stageSubtitle) : false;
  ctx.save();

  if (accentBars && hasLeft) {
    drawAccentBar(ctx, {
      x: margin,
      y: margin,
      width: Math.max(width * 0.3, 220),
      height: Math.max(28, topHeight * 0.55),
      angle: accentBars.angle || -12,
      color: accentBars.primary,
      alpha: accentBars.alpha || 0.2,
    });
  }

  if (accentBars && hasRight) {
    drawAccentBar(ctx, {
      x: width - margin - Math.max(width * 0.32, 260),
      y: height - margin - lowerHeight - 16,
      width: Math.max(width * 0.25, 200),
      height: Math.max(26, lowerHeight * 0.45),
      angle: -(accentBars.angle || -12),
      color: accentBars.secondary,
      alpha: accentBars.alpha || 0.2,
    });
  }

  const hasTimer = hasTimerOverlay.value;

  if (hasBrandingOverlay.value || hasTimer) {
    const badgeY = margin;
    const radius = topHeight / 2;
    if (hasBrandingOverlay.value && brandLogoImage.value) {
      const img = brandLogoImage.value;
      const paddingX = topHeight * 0.5;
      const paddingY = topHeight * 0.15;
      const imgHeight = brandLogoHasBadge.value ? topHeight : (topHeight - paddingY * 2);
      const imgWidth = imgHeight * (img.width / (img.height || 1));
      const badgeWidth = brandLogoHasBadge.value
        ? Math.min(imgWidth, width * 0.35)
        : Math.min(
          Math.max(topHeight * 1.6, imgWidth + paddingX * 2),
          width * 0.35
        );
      const badgeX = margin;
      if (!brandLogoHasBadge.value) {
        drawRoundedRectCustom(ctx, badgeX, badgeY, badgeWidth, topHeight, {
          tl: 0,
          bl: 0,
          tr: radius,
          br: radius,
        }, topBg);
      }
      ctx.drawImage(
        img,
        badgeX + (badgeWidth - imgWidth) / 2,
        badgeY + (topHeight - imgHeight) / 2,
        imgWidth,
        imgHeight
      );
    }

    if (hasBrandingOverlay.value && props.competitionName) {
      const text = String(props.competitionName).toUpperCase();
      ctx.font = `600 ${Math.max(18, topHeight * 0.42)}px 'Poppins', 'Helvetica', sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const textWidth = ctx.measureText(text).width;
      const badgeWidth = Math.min(width * 0.5, textWidth + topHeight * 1.4);
      const badgeX = width - margin - badgeWidth;
      drawRoundedRectCustom(ctx, badgeX, badgeY, badgeWidth, topHeight, {
        tl: radius,
        bl: radius,
        tr: 0,
        br: 0,
      }, topBg);
      ctx.fillStyle = topText;
      ctx.fillText(text, badgeX + badgeWidth / 2, badgeY + topHeight / 2);
    }

    if (hasTimer) {
      const timerMode = (props.timerMode || 'single').toLowerCase();
      const timerTextLeft = safeTimerValue(props.timerLeftValue || props.timerValue);
      const timerTextRight = safeTimerValue(props.timerRightValue || props.timerValue);
      const centerY = badgeY;
      const fontSize = Math.max(20, topHeight * 0.52);
      if (timerMode === 'vs') {
        const pillWidth = clamp(Math.round(width * 0.18), 140, 220);
        const vsWidth = clamp(Math.round(width * 0.06), 42, 72);
        const totalWidth = pillWidth * 2 + vsWidth + gap * 2;
        const startX = Math.max(margin + gap, (width - totalWidth) / 2);

        const leftX = startX;
        const rightX = startX + pillWidth + vsWidth + gap * 2;
        const vsX = startX + pillWidth + gap;

        drawRoundedRectCustom(ctx, leftX, centerY, pillWidth, topHeight, {
          tl: radius,
          bl: radius,
          tr: 10,
          br: 10,
        }, topBg);
        drawRoundedRectCustom(ctx, rightX, centerY, pillWidth, topHeight, {
          tl: 10,
          bl: 10,
          tr: radius,
          br: radius,
        }, topBg);
        drawRoundedRectCustom(ctx, vsX, centerY, vsWidth, topHeight, {
          tl: 8,
          bl: 8,
          tr: 8,
          br: 8,
        }, vsBgColor);

        ctx.fillStyle = topText;
        ctx.font = `700 ${fontSize}px 'Poppins', 'Helvetica', sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(timerTextLeft, leftX + pillWidth / 2, centerY + topHeight / 2);
        ctx.fillText(timerTextRight, rightX + pillWidth / 2, centerY + topHeight / 2);

        ctx.fillStyle = vsTextColor;
        ctx.font = `800 ${Math.max(18, topHeight * 0.42)}px 'Poppins', 'Helvetica', sans-serif`;
        ctx.fillText('VS', vsX + vsWidth / 2, centerY + topHeight / 2);
      } else {
        const badgeWidth = clamp(Math.round(width * 0.26), 160, 260);
        const badgeX = Math.max(margin + gap, (width - badgeWidth) / 2);
        drawRoundedRectCustom(ctx, badgeX, centerY, badgeWidth, topHeight, {
          tl: radius,
          bl: radius,
          tr: radius,
          br: radius,
        }, topBg);
        ctx.fillStyle = topText;
        ctx.font = `700 ${fontSize}px 'Poppins', 'Helvetica', sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(safeTimerValue(props.timerValue), badgeX + badgeWidth / 2, centerY + topHeight / 2);
      }
    }
  }

    if (hasLeft || hasRight || hasCenter) {
      const badgeY = height - margin - lowerHeight;
      if (hasVs) {
        const maxLeftWidth = clamp(width * 0.34, 320, 520);
        const maxRightWidth = clamp(width * 0.34, 320, 520);
        const maxCenterWidth = clamp(width * 0.22, 200, 320);
        let leftWidth = hasLeft ? maxLeftWidth : 0;
        let rightWidth = hasRight ? maxRightWidth : 0;
        const leftX = margin;
        const rightX = width - margin - rightWidth;
        const availableSpan = rightX - (leftX + leftWidth);
        let centerWidth = hasCenter ? Math.min(maxCenterWidth, availableSpan - gap * 2) : 0;
        if (centerWidth < 140 && hasCenter && availableSpan > gap * 2) {
          centerWidth = Math.min(Math.max(140, centerWidth), availableSpan - gap * 2);
        }
        const centerX = (leftX + leftWidth + rightX) / 2;
        const drawLeft = hasLeft && leftWidth > 0;
        const drawCenter = hasCenter && centerWidth > 0;
        const drawRight = hasRight && rightWidth > 0;

        if (drawLeft) {
          const badgeX = leftX;
          drawRoundedRectCustom(ctx, badgeX, badgeY, leftWidth, lowerHeight, {
            tl: 0,
            bl: 0,
            tr: 14,
            br: 14,
          }, lowerBg);
          const contentPadding = clamp(Math.round(lowerHeight * 0.28), 18, 28);
          let textStartX = badgeX + contentPadding;
          const flagHeight = props.vsLeftFlagUrl ? clamp(Math.round(lowerHeight * 0.42), 24, 36) : 0;
          const flagWidth = flagHeight ? Math.round(flagHeight * 1.4) : 0;
          const textAreaWidth = Math.max(80, leftWidth - contentPadding * 2 - flagWidth - (flagWidth ? 8 : 0));
          if (props.vsLeftFlagUrl) {
            drawFlagImage(
              ctx,
              props.vsLeftFlagUrl,
              textStartX,
              badgeY + (lowerHeight - flagHeight) / 2,
              flagWidth,
              flagHeight
            );
            textStartX += flagWidth + 8;
          }
          const athleteName = (props.vsLeftName || '').toUpperCase();
          ctx.textAlign = 'left';
          ctx.textBaseline = 'middle';
          ctx.fillStyle = lowerText;
          const nameBaseSize = Math.max(15, lowerHeight * 0.26);
          const { text: nameText, fontSize: nameFontSize } = fitTextSingleLine(
            ctx,
            athleteName,
            textAreaWidth,
            nameBaseSize,
            12
          );
          ctx.font = `700 ${nameFontSize}px 'Poppins', 'Helvetica', sans-serif`;
          ctx.fillText(nameText, textStartX, badgeY + lowerHeight / 2);
        }

        if (drawCenter) {
          const badgeX = centerX - centerWidth / 2;
          drawRoundedRectCustom(ctx, badgeX, badgeY, centerWidth, lowerHeight, {
            tl: 10,
            bl: 10,
            tr: 10,
            br: 10,
          }, lowerBg);
          ctx.fillStyle = accentColor;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          const stageFont = Math.max(15, lowerHeight * 0.25);
          const stagePadding = clamp(Math.round(lowerHeight * 0.28), 20, 34);
          const maxStageWidth = centerWidth - stagePadding * 2;
          const { text: stageText, fontSize: stageFontSize } = fitTextSingleLine(
            ctx,
            String(props.stageSubtitle || '').toUpperCase(),
            maxStageWidth,
            stageFont,
            12
          );
          ctx.font = `700 ${stageFontSize}px 'Poppins', 'Helvetica', sans-serif`;
          ctx.fillText(stageText, badgeX + centerWidth / 2, badgeY + lowerHeight / 2);
        }

        if (drawRight) {
          const badgeX = rightX;
          drawRoundedRectCustom(ctx, badgeX, badgeY, rightWidth, lowerHeight, {
            tl: 14,
            bl: 14,
            tr: 0,
            br: 0,
          }, lowerBg);
          const contentPadding = clamp(Math.round(lowerHeight * 0.28), 18, 28);
          const flagHeight = props.vsRightFlagUrl ? clamp(Math.round(lowerHeight * 0.42), 24, 36) : 0;
          const flagWidth = flagHeight ? Math.round(flagHeight * 1.4) : 0;
          const textAreaWidth = Math.max(80, rightWidth - contentPadding * 2 - flagWidth - (flagWidth ? 8 : 0));
          const nameValue = (props.vsRightName || '').toUpperCase();
          ctx.textAlign = 'right';
          ctx.textBaseline = 'middle';
          ctx.fillStyle = lowerText;
          const nameBaseSize = Math.max(15, lowerHeight * 0.26);
          const nameMaxX = badgeX + rightWidth - contentPadding - (flagWidth ? flagWidth + 8 : 0);
          const { text: rightText, fontSize: rightFontSize } = fitTextSingleLine(
            ctx,
            nameValue,
            textAreaWidth,
            nameBaseSize,
            12
          );
          ctx.font = `700 ${rightFontSize}px 'Poppins', 'Helvetica', sans-serif`;
          ctx.fillText(rightText, nameMaxX, badgeY + lowerHeight / 2);
          if (props.vsRightFlagUrl) {
            drawFlagImage(
              ctx,
              props.vsRightFlagUrl,
              badgeX + rightWidth - contentPadding - flagWidth,
              badgeY + (lowerHeight - flagHeight) / 2,
              flagWidth,
              flagHeight
            );
          }
        }
      } else {
        const maxLeftWidth = clamp(width * 0.5, 420, 520);
        const maxRightWidth = clamp(width * 0.32, 220, 320);
        let leftWidth = hasLeft ? maxLeftWidth : 0;
        let rightWidth = hasRight ? maxRightWidth : 0;
        const available = width - margin * 2 - gap * (hasLeft && hasRight ? 1 : 0);
        const requested = leftWidth + rightWidth;
        if (requested > available && requested > 0) {
          const scale = available / requested;
          leftWidth *= scale;
          rightWidth *= scale;
        }

        if (hasLeft) {
          const badgeX = margin;
          drawRoundedRectCustom(ctx, badgeX, badgeY, leftWidth, lowerHeight, {
            tl: 0,
            bl: 0,
            tr: 14,
            br: 14,
          }, lowerBg);
          const contentPadding = clamp(Math.round(lowerHeight * 0.28), 18, 28);
          let textStartX = badgeX + contentPadding;
          const flagHeight = props.athleteFlagUrl ? clamp(Math.round(lowerHeight * 0.42), 24, 36) : 0;
          const flagWidth = flagHeight ? Math.round(flagHeight * 1.4) : 0;
          const textAreaWidth = Math.max(80, leftWidth - contentPadding * 2 - flagWidth - (flagWidth ? 8 : 0));
          if (props.athleteFlagUrl) {
            drawFlagImage(
              ctx,
              props.athleteFlagUrl,
              textStartX,
              badgeY + (lowerHeight - flagHeight) / 2,
              flagWidth,
              flagHeight
            );
            textStartX += flagWidth + 8;
          }
          const athleteName = (props.athleteName || '').toUpperCase();
          ctx.textAlign = 'left';
          ctx.textBaseline = 'middle';
          ctx.fillStyle = lowerText;
          const nameBaseSize = Math.max(15, lowerHeight * 0.26);
          const { text: nameText, fontSize: nameFontSize } = fitTextSingleLine(
            ctx,
            athleteName,
            textAreaWidth,
            nameBaseSize,
            12
          );
          ctx.font = `700 ${nameFontSize}px 'Poppins', 'Helvetica', sans-serif`;
          ctx.fillText(nameText, textStartX, badgeY + lowerHeight / 2);
        }

        if (hasRight && rightWidth > 0) {
          const badgeX = width - margin - rightWidth;
          drawRoundedRectCustom(ctx, badgeX, badgeY, rightWidth, lowerHeight, {
            tl: 14,
            bl: 14,
            tr: 0,
            br: 0,
          }, lowerBg);
          ctx.fillStyle = accentColor;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          const stageFont = Math.max(15, lowerHeight * 0.25);
          const stagePadding = clamp(Math.round(lowerHeight * 0.3), 22, 36);
          const maxStageWidth = rightWidth - stagePadding * 2;
          const { text: stageText, fontSize: stageFontSize } = fitTextSingleLine(
            ctx,
            String(props.stageSubtitle || '').toUpperCase(),
            maxStageWidth,
            stageFont,
            12
          );
          ctx.font = `700 ${stageFontSize}px 'Poppins', 'Helvetica', sans-serif`;
          ctx.fillText(stageText, badgeX + rightWidth / 2, badgeY + lowerHeight / 2);
        }
      }
    }

  ctx.restore();
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function drawRoundedRectCustom(ctx, x, y, width, height, radii = {}, fillStyle) {
  const r = {
    tl: Math.max(0, Math.min(radii.tl ?? 0, width / 2, height / 2)),
    tr: Math.max(0, Math.min(radii.tr ?? 0, width / 2, height / 2)),
    br: Math.max(0, Math.min(radii.br ?? 0, width / 2, height / 2)),
    bl: Math.max(0, Math.min(radii.bl ?? 0, width / 2, height / 2)),
  };
  ctx.beginPath();
  ctx.moveTo(x + r.tl, y);
  ctx.lineTo(x + width - r.tr, y);
  if (r.tr) {
    ctx.quadraticCurveTo(x + width, y, x + width, y + r.tr);
  } else {
    ctx.lineTo(x + width, y);
  }
  ctx.lineTo(x + width, y + height - r.br);
  if (r.br) {
    ctx.quadraticCurveTo(x + width, y + height, x + width - r.br, y + height);
  } else {
    ctx.lineTo(x + width, y + height);
  }
  ctx.lineTo(x + r.bl, y + height);
  if (r.bl) {
    ctx.quadraticCurveTo(x, y + height, x, y + height - r.bl);
  } else {
    ctx.lineTo(x, y + height);
  }
  ctx.lineTo(x, y + r.tl);
  if (r.tl) {
    ctx.quadraticCurveTo(x, y, x + r.tl, y);
  } else {
    ctx.lineTo(x, y);
  }
  ctx.closePath();
  ctx.fillStyle = fillStyle;
  ctx.fill();
}

function roundedRectPath(ctx, x, y, width, height, radius = 0) {
  const safeRadius = Math.max(0, Math.min(radius, width / 2, height / 2));
  ctx.beginPath();
  ctx.moveTo(x + safeRadius, y);
  ctx.lineTo(x + width - safeRadius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + safeRadius);
  ctx.lineTo(x + width, y + height - safeRadius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - safeRadius, y + height);
  ctx.lineTo(x + safeRadius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - safeRadius);
  ctx.lineTo(x, y + safeRadius);
  ctx.quadraticCurveTo(x, y, x + safeRadius, y);
  ctx.closePath();
}

function drawFlagImage(ctx, url, x, y, width, height) {
  if (!url || !width || !height) return false;
  const img = getOverlayFlagImage(url);
  if (!img) return false;
  const sourceWidth = img.naturalWidth || img.width || width;
  const sourceHeight = img.naturalHeight || img.height || height;
  const scale = Math.max(width / sourceWidth, height / sourceHeight);
  const drawWidth = sourceWidth * scale;
  const drawHeight = sourceHeight * scale;
  const offsetX = x + (width - drawWidth) / 2;
  const offsetY = y + (height - drawHeight) / 2;
  ctx.save();
  roundedRectPath(ctx, x, y, width, height, 4);
  ctx.clip();
  ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
  ctx.restore();
  return true;
}

function drawAccentBar(ctx, { x, y, width, height, angle = -10, color = '#f97316', alpha = 0.2 }) {
  const rad = (angle * Math.PI) / 180;
  const centerX = x + width / 2;
  const centerY = y + height / 2;
  ctx.save();
  ctx.translate(centerX, centerY);
  ctx.rotate(rad);
  ctx.fillStyle = applyAlpha(color, alpha);
  ctx.fillRect(-width / 2, -height / 2, width, height);
  ctx.restore();
}

function applyAlpha(color, alpha) {
  if (!color) return `rgba(0,0,0,${alpha || 0.2})`;
  if (color.startsWith('#')) {
    const hex = color.replace('#', '');
    const bigint = parseInt(hex.length === 3 ? hex.split('').map((c) => c + c).join('') : hex, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha || 0.2})`;
  }
  if (color.startsWith('rgb')) {
    return color.replace(/rgb(a?)\(([^)]+)\)/, (match, a, vals) => {
      const parts = vals.split(',').map((v) => v.trim()).slice(0, 3);
      return `rgba(${parts.join(', ')}, ${alpha || 0.2})`;
    });
  }
  return color;
}

function fitTextSingleLine(ctx, text, maxWidth, baseSize, minSize = 12, fontFamily = "'Poppins', 'Helvetica', sans-serif", fontWeight = '700') {
  const clean = (text || '').trim();
  let size = baseSize;
  let content = clean;
  const applyFont = () => {
    ctx.font = `${fontWeight} ${size}px ${fontFamily}`;
  };
  applyFont();

  while (size > minSize && ctx.measureText(content).width > maxWidth) {
    size -= 1;
    applyFont();
  }

  if (ctx.measureText(content).width > maxWidth) {
    const ellipsis = '…';
    while (content.length > 1 && ctx.measureText(`${content}${ellipsis}`).width > maxWidth) {
      content = content.slice(0, -1);
    }
    content = `${content.trim()}${ellipsis}`;
  }

  return {
    text: content || clean,
    fontSize: size,
  };
}

function handleBeginRecording() {
  if (props.hasRunningTimer) return;
  if (!stream.value) {
    error.value = 'Activa la cámara antes de grabar.';
    return;
  }
  if (!hasMediaRecorder) {
    error.value = 'La grabación no es compatible con este navegador.';
    return;
  }
  if (recordingActive.value || finalizingRecording.value) return;

  error.value = '';
  finalizingRecording.value = false;
  const started = startRecorder();
  if (started) {
    emit('begin-recording', {
      deviceId: selectedDeviceId.value,
      stream: lastUsedRecorderStream || getRecordingStream(),
      quality: selectedQuality.value,
    });
  }
}

function handleStopCamera() {
  if (!stream.value || recordingActive.value || finalizingRecording.value) {
    return;
  }
  stopRecorder('cleanup');
  stopStream('stop-camera-button');
}

function handleFinishRecording() {
  if (props.hasRunningTimer) return;
  if (!recordingActive.value || finalizingRecording.value) return;
  recordingActive.value = false;
  finalizingRecording.value = true;
  stopRecorder('user');
}

function handleClose() {
  stopRecorder('cancel');
  stopStream('close-button');
  recordingActive.value = false;
  finalizingRecording.value = false;
  emit('cancel');
}

function attemptAutoRecord() {
  if (!stream.value || recordingActive.value || finalizingRecording.value) return;
  handleBeginRecording();
}

function stopStream() {
  const hadStream = Boolean(stream.value);
  teardownComposition();
  stopTrackMonitor();
  stopRecordingTimer();
  if (stream.value) {
    stream.value.getTracks().forEach((track) => track.stop());
    stream.value = null;
  }
  if (videoEl.value) {
    try {
      videoEl.value.pause?.();
    } catch (err) {
      /* ignored */
    }
    videoEl.value.srcObject = null;
  }
  lastUsedRecorderStream = null;
  recordingActive.value = false;
  finalizingRecording.value = false;
  if (hadStream) {
    emit('camera-stopped');
  }
}

onBeforeUnmount(() => {
  if (!props.keepStreamOnHide) {
    stopRecorder('cleanup');
    stopStream('unmount');
  }
  if (orientationMediaQuery) {
    orientationMediaQuery.removeEventListener('change', updateOrientation);
  }
  if (typeof window !== 'undefined') {
    window.removeEventListener('orientationchange', computeIsLandscape);
    if (screenOrientationHandle?.removeEventListener) {
      screenOrientationHandle.removeEventListener('change', computeIsLandscape);
    }
  }
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', updateViewportFlags);
  }
  if (typeof visualViewport !== 'undefined') {
    visualViewport.removeEventListener('resize', computeIsLandscape);
  }
});

function resolveMimeType() {
  if (!hasMediaRecorder) {
    return '';
  }
  const Recorder = window.MediaRecorder;
  if (typeof Recorder?.isTypeSupported !== 'function') {
    return '';
  }
  for (const type of MIME_PRIORITY) {
    if (Recorder.isTypeSupported(type)) {
      return type;
    }
  }
  return '';
}

function startRecordingTimer() {
  stopRecordingTimer();
  recordingStartedAt.value = Date.now();
  updateRecordingElapsed();
  recordingTimerHandle = window.setInterval(updateRecordingElapsed, 500);
}

function stopRecordingTimer() {
  if (recordingTimerHandle) {
    clearInterval(recordingTimerHandle);
    recordingTimerHandle = null;
  }
  recordingStartedAt.value = 0;
  recordingElapsedText.value = '00:00';
}

function updateRecordingElapsed() {
  if (!recordingActive.value || !recordingStartedAt.value) {
    recordingElapsedText.value = '00:00';
    return;
  }
  const elapsedMs = Date.now() - recordingStartedAt.value;
  const totalSeconds = Math.max(0, Math.floor(elapsedMs / 1000));
  const minutes = Math.floor(totalSeconds / 60)
    .toString()
    .padStart(2, '0');
  const seconds = (totalSeconds % 60).toString().padStart(2, '0');
  recordingElapsedText.value = `${minutes}:${seconds}`;
}

function estimateRecordingBitrate() {
  if (!recordingStartedAt.value || !recordedSizeBytes.value) return 0;
  const elapsedMs = Date.now() - recordingStartedAt.value;
  if (elapsedMs <= 0) return 0;
  return (recordedSizeBytes.value * 8 * 1000) / elapsedMs;
}

function startRecorder() {
  const recordingSource = getRecordingStream();
  if (!recordingSource) {
    error.value = 'Activa la cámara antes de grabar.';
    return false;
  }
  recordedChunks.value = [];
  recordedSizeBytes.value = 0;
  const preferredMime = resolveMimeType();
  const qualityPreset = getSelectedQualityPreset();
  const recorderProfile = qualityPreset?.recorder || {};
  const options = {
    ...(preferredMime ? { mimeType: preferredMime } : {}),
    ...(recorderProfile.videoBitsPerSecond ? { videoBitsPerSecond: recorderProfile.videoBitsPerSecond } : {}),
  };
  const hasOptions = Object.keys(options).length > 0;

  let recorder;
  try {
    const Recorder = window.MediaRecorder;
    recorder = hasOptions ? new Recorder(recordingSource, options) : new Recorder(recordingSource);
  } catch (err) {
    error.value = 'No se pudo iniciar la grabación.';
    return false;
  }

  lastUsedRecorderStream = recordingSource;

  recordingMime.value = recorder.mimeType || preferredMime || 'video/webm';
  stopReason.value = 'user';

  recorder.ondataavailable = (event) => {
    if (event.data && event.data.size) {
      recordedChunks.value.push(event.data);
      recordedSizeBytes.value += event.data.size;
    }
  };

  recorder.onerror = () => {
    error.value = 'Se produjo un error durante la grabación.';
    recordingActive.value = false;
    stopRecorder('error');
  };

  recorder.onstop = () => {
    handleRecorderStop(recorder);
  };

  recorder.start(1000);
  mediaRecorder.value = recorder;
  recordingActive.value = true;
  startRecordingTimer();
  return true;
}

function stopRecorder(reason = 'user') {
  if (!mediaRecorder.value) return;
  if (mediaRecorder.value.state === 'inactive') {
    mediaRecorder.value = null;
    recordingActive.value = false;
    stopRecordingTimer();
    finalizingRecording.value = false;
    return;
  }
  stopReason.value = reason;
  recordingActive.value = false;
  stopRecordingTimer();
  if (reason !== 'user') {
    finalizingRecording.value = false;
  } else if (typeof mediaRecorder.value.requestData === 'function') {
    try {
      mediaRecorder.value.requestData();
    } catch (requestErr) {
      /* ignored */
    }
  }
  try {
    mediaRecorder.value.stop();
  } catch (err) {
    mediaRecorder.value = null;
    finalizingRecording.value = false;
  }
}

function handleRecorderStop(recorderInstance) {
  finalizingRecording.value = false;
  const reason = stopReason.value;
  const mimeType = recordingMime.value || recorderInstance?.mimeType || 'video/webm';
  const chunks = recordedChunks.value.slice();
  recordedChunks.value = [];
  mediaRecorder.value = null;
  recordingActive.value = false;

  if (reason !== 'user') {
    stopStream('recorder-stop-' + reason);
    return;
  }

  if (!chunks.length) {
    error.value = props.noDataLabel || DEFAULT_NO_DATA_ERROR;
    return;
  }

  const blob = new Blob(chunks, { type: mimeType });
  const fileName = resolveRecordingFileName(mimeType);
  let file = null;
  try {
    file = new File([blob], fileName, { type: mimeType });
  } catch (err) {
    /* ignored, Blob fallback */
  }

  downloadRecording(blob, fileName);
  emit('finish-recording', {
    deviceId: selectedDeviceId.value,
    stream: lastUsedRecorderStream || getRecordingStream(),
    blob,
    file,
    mimeType,
    fileName,
    quality: selectedQuality.value,
  });
}

async function getPreferredStream() {
  return requestUserMedia();
}

function buildVideoConstraint() {
  if (selectedDeviceId.value) {
    return {
      deviceId: { exact: selectedDeviceId.value },
      aspectRatio: { ideal: 16 / 9 },
    };
  }
  // Prefer rear camera on mobile; browser will fall back if unavailable
  return {
    facingMode: { ideal: 'environment' },
    width: { ideal: 1920 },
    height: { ideal: 1080 },
    aspectRatio: { ideal: 16 / 9 },
  };
}

async function requestUserMedia() {
  const videoConstraint = buildVideoConstraint();
  const constraints = { video: videoConstraint };
  const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
  await applyVideoConstraints(mediaStream);
  return mediaStream;
}

async function applyVideoConstraints(mediaStream) {
  const preset = getSelectedQualityPreset();
  const constraints = preset?.constraints || {
    width: { ideal: 1920 },
    height: { ideal: 1080 },
    aspectRatio: { ideal: 16 / 9 },
  };
  const track = mediaStream?.getVideoTracks?.()[0];
  if (!track || typeof track.applyConstraints !== 'function') return;
  try {
    await track.applyConstraints(constraints);
  } catch (err) {
    /* ignored */
  }
}

function updateTrackDiagnostics(currentStream = stream.value) {
  const videoTrack = currentStream?.getVideoTracks?.()[0];
  if (!videoTrack) {
    trackCapabilities.value = null;
    trackSettings.value = null;
    return;
  }
  try {
    trackCapabilities.value = typeof videoTrack.getCapabilities === 'function'
      ? videoTrack.getCapabilities()
      : null;
  } catch (err) {
    trackCapabilities.value = null;
  }
  try {
    trackSettings.value = typeof videoTrack.getSettings === 'function'
      ? videoTrack.getSettings()
      : null;
  } catch (err) {
    trackSettings.value = null;
  }
}

function startTrackMonitor(currentStream = stream.value) {
  stopTrackMonitor();
  if (!currentStream) {
    return;
  }
  updateTrackDiagnostics(currentStream);
  trackMonitorHandle = window.setInterval(() => updateTrackDiagnostics(currentStream), 1000);
}

function stopTrackMonitor() {
  if (trackMonitorHandle) {
    clearInterval(trackMonitorHandle);
    trackMonitorHandle = null;
  }
  trackCapabilities.value = null;
  trackSettings.value = null;
}

function getSelectedQualityPreset() {
  return QUALITY_PRESETS[selectedQuality.value] || QUALITY_PRESETS.pro || QUALITY_PRESETS.max;
}

function buildRecordingFileName(mimeType) {
  const extension = mimeType.includes('mp4') ? 'mp4' : 'webm';
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
  return `camera-recording-${timestamp}.${extension}`;
}

function resolveRecordingFileName(mimeType) {
  const extension = mimeType.includes('mp4') ? 'mp4' : 'webm';
  const rawName = (props.recordingFileName || '').trim();
  if (!rawName) {
    return buildRecordingFileName(mimeType);
  }
  const safeName = sanitizeFileName(rawName);
  if (!safeName) {
    return buildRecordingFileName(mimeType);
  }
  const lower = safeName.toLowerCase();
  if (lower.endsWith(`.${extension}`)) {
    return safeName;
  }
  return `${safeName}.${extension}`;
}

function sanitizeFileName(value) {
  return String(value)
    .replace(/[\\/:*?"<>|]+/g, '-')
    .replace(/\s+/g, ' ')
    .trim();
}

function formatBytes(bytes) {
  if (bytes === 0) return '0 B';
  if (!bytes) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  const decimals = value >= 100 ? 0 : value >= 10 ? 1 : 2;
  return `${value.toFixed(decimals)} ${units[unitIndex]}`;
}

function formatBitrate(bps) {
  if (bps === 0) return '0 kbps';
  if (!bps) return '';
  const mbps = bps / 1_000_000;
  if (mbps >= 1) {
    return `${mbps.toFixed(mbps >= 10 ? 0 : 1)} Mbps`;
  }
  const kbps = bps / 1000;
  return `${kbps.toFixed(kbps >= 10 ? 0 : 1)} kbps`;
}

function downloadRecording(blob, fileName) {
  if (typeof document === 'undefined' || typeof URL === 'undefined') return;
  if (!document.body) return;
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}
</script>

<style scoped>
.camera-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.camera-modal-card {
  background: #fff;
  border-radius: 10px;
  width: 90%;
  max-width: 720px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-direction: column;
  padding: 14px 16px;
  max-height: calc(100vh - 16px);
  overflow: hidden;
}

.camera-modal-card--recording {
  outline: 3px solid #dc3545;
  box-shadow: 0 0 18px rgba(220, 53, 69, 0.7), 0 0 32px rgba(220, 53, 69, 0.35);
  transform: translateY(-3px);
  animation: camera-recording-pulse 1.35s ease-in-out infinite;
}

@keyframes camera-recording-pulse {
  0% {
    box-shadow: 0 0 12px rgba(220, 53, 69, 0.45), 0 0 22px rgba(220, 53, 69, 0.22);
  }
  50% {
    box-shadow: 0 0 22px rgba(220, 53, 69, 0.82), 0 0 38px rgba(220, 53, 69, 0.42);
  }
  100% {
    box-shadow: 0 0 12px rgba(220, 53, 69, 0.45), 0 0 22px rgba(220, 53, 69, 0.22);
  }
}

.camera-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.header-actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.camera-modal-title {
  margin: 0;
  font-size: 1.05rem;
}

.camera-modal-close {
  border: none;
  background: transparent;
  font-size: 1.2rem;
  cursor: pointer;
}

.camera-modal-help {
  margin: 0 0 10px 0;
  color: #495057;
  font-size: 0.95rem;
}

.style-toggle {
  background: #f1f3f5;
  border: 1px solid #dcdfe4;
  border-radius: 6px;
  padding: 6px 8px;
  cursor: pointer;
  color: #333;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.style-toggle.test-toggle {
  color: #0d6efd;
}

.style-toggle.live-toggle {
  color: #6c757d;
}

.style-toggle.live-toggle.live-toggle--active {
  color: #dc3545;
  border-color: rgba(220, 53, 69, 0.35);
  background: #fff5f5;
}

.style-toggle--disabled {
  opacity: 0.4;
  cursor: not-allowed;
  pointer-events: none;
}

.style-toggle:hover {
  background: #e9ecef;
}

.bandwidth-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1200;
}

.bandwidth-modal-card {
  background: #fff;
  border-radius: 12px;
  padding: 16px 18px;
  width: 420px;
  box-shadow: 0 12px 32px rgba(0,0,0,0.25);
}

.bandwidth-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.bandwidth-modal-body {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.bandwidth-close {
  border: none;
  background: transparent;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
}

.bandwidth-hint {
  margin: 0;
  font-size: 0.9rem;
  color: #4b5563;
}

.bandwidth-error {
  background: #fff1f2;
  color: #b91c1c;
  border: 1px solid #fecdd3;
  padding: 8px 10px;
  border-radius: 8px;
}

.bandwidth-loading {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #0f172a;
}

.bandwidth-result {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.result-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.95rem;
}

.bandwidth-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 6px;
}

.camera-modal-label {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 10px;
  font-weight: 600;
}

.camera-selectors {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.camera-selectors .camera-modal-label {
  margin-bottom: 0;
}

.camera-info-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 2px;
}

.camera-info-bar--inline {
  flex: 1 1 auto;
  margin-right: 12px;
  margin-bottom: 0;
}

.camera-action-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
}

.camera-action-row .action-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
}

.info-chip {
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  gap: 4px;
  padding: 6px 8px;
  border-radius: 8px;
  background: #f8fafc;
  color: #0f172a;
  font-size: 12.5px;
  line-height: 1.25;
  border: 1px solid #e2e8f0;
  flex: 0 0 auto;
  min-width: 0;
}

.info-chip--compact {
  padding: 4px 6px;
  font-size: 12px;
}

.info-chip__dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #94a3b8;
  box-shadow: 0 0 0 2px rgba(148, 163, 184, 0.2);
}

.info-chip__label {
  font-weight: 600;
}

.info-chip__title {
  font-weight: 600;
  color: #0f172a;
  white-space: nowrap;
}

.info-chip__value {
  color: #334155;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.info-chip__timer {
  margin-left: auto;
  font-variant-numeric: tabular-nums;
  font-weight: 700;
}

.info-chip--muted {
  background: #f1f5f9;
  color: #475569;
}

.info-chip--warn {
  border-color: #f97316;
  background: #fff7ed;
  color: #9a3412;
}

.info-chip--warn .info-chip__value {
  color: #9a3412;
}

.info-chip--live {
  background: #ecfeff;
  border-color: #06b6d4;
  color: #0f172a;
}

.info-chip--recording {
  background: #fef2f2;
  border-color: #ef4444;
  color: #991b1b;
}

.info-chip--recording .info-chip__dot {
  background: #ef4444;
  box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.15);
}

.info-chip--live .info-chip__dot {
  background: #06b6d4;
  box-shadow: 0 0 0 4px rgba(6, 182, 212, 0.2);
}

.info-chip--idle .info-chip__dot {
  background: #94a3b8;
}

.info-chip--status {
  flex-direction: row;
  align-items: center;
  gap: 6px;
}

.info-chip--status .info-chip__label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

@media (max-width: 640px) {
  .camera-info-bar {
    gap: 6px;
  }

  .camera-preview {
    min-height: 180px;
    max-height: 60vh;
  }

  .camera-modal-card {
    width: 96%;
    padding: 12px;
    max-height: calc(100vh - 10px);
  }
}

.camera-modal-select {
  padding: 8px 10px;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-size: 0.95rem;
}

.preview-stack {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.camera-preview {
  position: relative;
  width: 100%;
  max-width: 100%;
  background: #111;
  border-radius: 8px;
  overflow: hidden;
  min-height: 220px;
  max-height: 70vh;
  aspect-ratio: 16 / 9;
}

.camera-preview video {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: contain;
}

.camera-preview-canvas {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: contain;
}

.camera-preview-video {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}

.camera-overlay {
  position: absolute;
  inset: 0;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 0;
  --camera-overlay-top-bg: rgba(0, 0, 0, 0.55);
  --camera-overlay-top-text: #fff;
  --camera-overlay-lower-bg: rgba(7, 16, 45, 0.78);
  --camera-overlay-lower-text: #fff;
  --camera-overlay-country-color: rgba(255, 255, 255, 0.85);
  --camera-overlay-accent: #fff;
}

.camera-overlay-row {
  display: flex;
  width: 100%;
  padding: 0;
}

.camera-overlay-row--top {
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}

.camera-overlay-row--bottom {
  justify-content: space-between;
  align-items: flex-end;
  gap: 10px;
  flex-wrap: wrap;
}

.camera-overlay-badge {
  min-width: 120px;
  max-width: 45%;
  background: var(--camera-overlay-top-bg);
  color: var(--camera-overlay-top-text);
  border-radius: 12px;
  padding: 8px 14px;
  font-size: 0.85rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  text-align: center;
}

.camera-overlay-badge img {
  height: 32px;
  width: auto;
  object-fit: contain;
}

.camera-overlay-badge--embedded {
  min-width: 0;
  background: transparent;
  padding: 0;
}

.camera-overlay-badge--embedded img {
  height: 48px;
}

.camera-overlay-badge--right {
  margin-left: auto;
}
.camera-overlay-badge--center {
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  box-shadow: none;
  padding: 0;
}

.timer-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: var(--camera-overlay-top-bg);
  color: var(--camera-overlay-top-text);
  border-radius: 999px;
  padding: 6px 16px;
  font-weight: 700;
  font-size: 1rem;
  font-variant-numeric: tabular-nums;
  min-width: 120px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.18);
}

.timer-pill--vs {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: transparent;
  padding: 0;
  box-shadow: none;
}

.timer-pill__side {
  background: var(--camera-overlay-top-bg);
  color: var(--camera-overlay-top-text);
  padding: 6px 12px;
  border-radius: 999px;
  min-width: 110px;
  text-align: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.18);
}

.timer-pill__vs {
  background: linear-gradient(135deg, #f7fafc, rgba(255, 255, 255, 0.94));
  color: #0b132b;
  font-weight: 800;
  padding: 6px 10px;
  border-radius: 999px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.6);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  min-width: 48px;
  text-align: center;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.18);
}

:deep(.camera-preview--dark) .timer-pill__vs,
:deep(.camera-preview--custom) .timer-pill__vs {
  background: var(--camera-overlay-top-bg);
  color: var(--camera-overlay-top-text);
  border-color: rgba(255, 255, 255, 0.25);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.35);
}

.camera-overlay-badge--left {
  border-radius: 0 12px 12px 0;
  margin-left: 0;
}

.camera-overlay-badge--left.camera-overlay-badge--embedded {
  border-radius: 0;
}

.camera-overlay-badge--right {
  border-radius: 12px 0 0 12px;
  margin-right: 0;
}

.camera-overlay-lower {
  min-width: 180px;
  max-width: 56%;
  background: var(--camera-overlay-lower-bg);
  padding: 10px 14px;
  border-radius: 14px;
  display: inline-flex;
  gap: 8px;
  align-items: center;
  color: var(--camera-overlay-lower-text);
  text-transform: uppercase;
  letter-spacing: 0.03em;

  box-shadow: 0 5px 18px rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(4px);
}


.camera-overlay-lower--left {
  justify-content: flex-start;
  margin-right: auto;
  border-radius: 0 14px 14px 0;
  max-width: 55%;
}

.camera-overlay-lower--center {
  margin-inline: auto;
  justify-content: center;
  text-align: center;
  border-radius: 10px;
  min-width: 136px;
  padding-inline: 18px;
}

.camera-overlay-lower--right {
  margin-left: auto;
  justify-content: flex-end;
  padding-inline: 16px;
  border-radius: 14px 0 0 14px;
  white-space: nowrap;
  max-width: 55%;
}

.camera-overlay-flag-image {
  width: 34px;
  height: 24px;
  object-fit: cover;
  border-radius: 4px;
  flex: 0 0 auto;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.25);
}

.camera-overlay-flag-image--right {
  margin-left: 6px;
}

.camera-overlay-info {
  display: flex;
  flex-direction: row;
  align-items: center;
  line-height: 1.1;
  gap: 8px;
  max-width: 100%;
  width: 100%;
  min-width: 0;
}

.camera-overlay-info--right {
  justify-content: flex-end;
  text-align: right;
}

.camera-overlay-info__name {
  font-size: 0.86rem;
  font-weight: 700;
  text-transform: uppercase;
  white-space: nowrap;
  word-break: normal;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
  max-width: 100%;
}

.camera-overlay-info__country {
  font-size: 0.75rem;
  color: var(--camera-overlay-country-color);
  text-transform: uppercase;
  white-space: normal;
}

.camera-overlay-info__value {
  font-size: 0.88rem;
  font-weight: 700;
  color: var(--camera-overlay-accent);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.camera-preview-placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #adb5bd;
  font-size: 0.95rem;
}

.camera-preview--light {
  background: linear-gradient(120deg, #f8fafc, #e2e8f0);
}

.camera-preview--dark {
  background: #020617;
}

.camera-preview--custom {
  background: radial-gradient(circle at 20% 20%, #0f172a, #020617 60%);
}

.camera-preview--empty {
  background: #1f1f1f;
}

.camera-preview--fullscreen {
  position: fixed;
  inset: 0;
  z-index: 1200;
  width: 100vw;
  height: 100vh;
  max-height: none;
  min-height: 0;
  border-radius: 0;
  background: #000;
  aspect-ratio: auto;
}

.camera-preview--fullscreen .camera-preview-canvas,
.camera-preview--fullscreen video {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.camera-modal-error {
  margin-bottom: 10px;
  padding: 8px 10px;
  border-radius: 6px;
  background: #fff4f4;
  color: #b00020;
  border: 1px solid #f5c2c7;
}

.camera-modal-actions {
  display: flex;
  gap: 12px;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 12px;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.btn-stop-camera {
  border-color: #2563eb;
  color: #2563eb;
}

.btn-stop-camera:hover,
.btn-stop-camera:focus {
  border-color: #1d4ed8;
  color: #f8f9fb;
}

.btn-record {
  background: #dc2626;
  border-color: #dc2626;
}

.btn-record:hover,
.btn-record:focus {
  background: #b91c1c;
  border-color: #b91c1c;
}
</style>
